import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { Loader2, Shield, Lock, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { TwoFactorVerifyModal } from '@/components/security';
import { redirectPersistenceService } from '@/services/redirectPersistence';
import '@/styles/marketing.css';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState<string | undefined>();

  // Invitation resend state
  const [showResendForm, setShowResendForm] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  // Check for deactivation banner from session storage (set by api.ts on token refresh failure)
  const [deactivationMessage, setDeactivationMessage] = useState<string | null>(null);

  useEffect(() => {
    const reason = sessionStorage.getItem('deactivation_reason');
    if (reason === 'org_deactivated') {
      setDeactivationMessage('Your organization has been deactivated. Please contact your administrator to regain access.');
      sessionStorage.removeItem('deactivation_reason');
    }
  }, []);

  const { login, verify2FA, twoFactorPending, cancelTwoFactor } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    try {
      const result = await login(email, password);
      if (!result.requiresTwoFactor) {
        // Check for stored redirect URL (deep linking support)
        const redirectUrl = redirectPersistenceService.getAndClearRedirectUrl(useAuth.getState().user?.id);
        navigate(redirectUrl || '/dashboard');
      }
      // If 2FA is required, the modal will be shown automatically
    } catch (error) {
      // Error is handled by the auth hook
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FAVerify = async (token: string, isBackupCode: boolean) => {
    setTwoFactorError(undefined);
    try {
      await verify2FA(token, isBackupCode);
      // Check for stored redirect URL (deep linking support)
      const redirectUrl = redirectPersistenceService.getAndClearRedirectUrl(useAuth.getState().user?.id);
      navigate(redirectUrl || '/dashboard');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Verification failed';
      setTwoFactorError(message);
      throw error;
    }
  };

  const handleCancel2FA = () => {
    cancelTwoFactor();
    setTwoFactorError(undefined);
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-content">
          {/* Logo/Branding */}
          <div className="login-logo">
            <img
              src="/images/amplify-logo.png"
              alt="Amplify Ambient Listening, powered by Harris CareTracker"
              className="login-logo-img"
            />
          </div>

          {/* Header */}
          <div className="login-header">
            <h2 className="login-title">Welcome Back</h2>
            <p className="login-description">
              Sign in to access your clinical workspace
            </p>
          </div>

          {/* Deactivation Banner */}
          {deactivationMessage && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 mb-4">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-amber-800">{deactivationMessage}</p>
              </div>
            </div>
          )}

          {/* Login Form */}
          <form className="login-form" onSubmit={handleSubmit}>
            <div className="login-form-fields">
              <div className="login-form-group">
                <label htmlFor="email" className="login-form-label">
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="login-form-input"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="login-form-group">
                <label htmlFor="password" className="login-form-label">
                  Password
                </label>
                <div className="login-password-wrapper">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    className="login-form-input login-password-input"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="login-password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="login-password-icon" />
                    ) : (
                      <EyeIcon className="login-password-icon" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="login-form-options">
              <div className="login-remember">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="login-checkbox"
                />
                <label htmlFor="remember-me" className="login-checkbox-label">
                  Remember me
                </label>
              </div>

              <Link to="/forgot-password" className="login-forgot-link">
                Forgot password?
              </Link>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="login-submit-button"
            >
              {isLoading ? (
                <>
                  <Loader2 className="login-spinner" size={20} />
                  Signing in...
                </>
              ) : (
                <>
                  Sign In
                  <ArrowRight size={20} />
                </>
              )}
            </button>

            <div className="login-register-prompt">
              <p className="login-register-text">
                Need access?{' '}
                <Link to="/contact" className="login-register-link">
                  Contact your administrator
                </Link>
              </p>
            </div>

            {/* Invitation resend self-service */}
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => { setShowResendForm(!showResendForm); setResendMessage(null); }}
                className="text-sm text-purple-600 hover:text-purple-800 underline"
              >
                Didn't receive your invitation?
              </button>
              {showResendForm && (
                <div className="mt-3 p-4 bg-gray-50 rounded-lg text-left">
                  <p className="text-sm text-gray-600 mb-3">
                    Enter your email and we'll resend your invitation if one exists.
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                    />
                    <button
                      type="button"
                      disabled={resendLoading || !resendEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(resendEmail.trim())}
                      onClick={async () => {
                        setResendLoading(true);
                        setResendMessage(null);
                        try {
                          const response = await fetch(`${API_URL}/api/v1/auth/resend-invitation`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ email: resendEmail.trim() })
                          });
                          const data = await response.json();
                          setResendMessage(data.message || 'If an invitation exists, it has been resent.');
                        } catch {
                          setResendMessage('Something went wrong. Please try again later.');
                        } finally {
                          setResendLoading(false);
                        }
                      }}
                      className="px-4 py-2 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 disabled:opacity-50"
                    >
                      {resendLoading ? 'Sending...' : 'Resend'}
                    </button>
                  </div>
                  {resendMessage && (
                    <p className="mt-2 text-sm text-green-700">{resendMessage}</p>
                  )}
                </div>
              )}
            </div>
          </form>

          {/* Security Notice */}
          <div className="login-security-notice">
            <div className="login-security-icons">
              <Shield size={20} />
              <Lock size={20} />
            </div>
            <p className="login-security-text">
              HIPAA-compliant platform with end-to-end encryption and secure data handling
            </p>
          </div>
        </div>
      </div>

      {/* Two-Factor Authentication Modal */}
      <TwoFactorVerifyModal
        isOpen={!!twoFactorPending}
        onClose={handleCancel2FA}
        onVerify={handle2FAVerify}
        partialToken={twoFactorPending?.partialToken || ''}
        error={twoFactorError}
      />
    </div>
  );
};

export default Login;